import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { SaleContext, SaleProduct, SaleStudent } from "../../../../services/v2/sales.service";
import KitPaymentPrompt from "../../../../components/kits/kitPaymentPrompt";
import MultiSelect from "../../../../components/kits/multiSelect";
import { FIELD_FONT } from "../../../../components/kits/kitUi";
import { apiError, ui, useCampus, useDebounced, useIdleLocked, usePerms } from "../kits/shared";

/*
	Tienda → Nueva Venta (sección "newSale", fase 2, 2026-10-01). Convive con Catálogo.
	Alumno → kit de su curso propuesto → productos por buscador o lector → quién vendió (equipo de kits o maestro) → nota →
	se carga a su cuenta y Caja cobra como siempre (o se reporta el pago por link/depósito).
*/

/**
 * size: talla escrita a mano (kits viejos sin piezas con variantes). variantId: la talla/color elegida de un producto suelto
 * (decide el precio si tiene precio propio). choices: talla/color de cada pieza del kit {modelo: variante}.
 */
interface CartLine { product: SaleProduct; quantity: number; size: string; variantId: number | null; choices: Record<number, number> }

const variantsOf = (product: SaleProduct) => product.variants || [];
/** Precio de la línea: el de la variante elegida (puede ser propio) o el del producto. */
const linePrice = (line: CartLine): number => {
	const variant = variantsOf(line.product).find((option) => option.id === line.variantId);
	return variant ? variant.price : line.product.price;
};
/** Las variantes no cuestan todas lo mismo: hay que elegir antes de cobrar. */
const mustPick = (line: CartLine): boolean => line.variantId === null && new Set(variantsOf(line.product).map((option) => option.price)).size > 1;

const NuevaVenta: React.FC = () => {
	const { campusId, currency } = useCampus();
	const idleLocked = useIdleLocked();
	const perms: any = usePerms("newSale");
	const http = HttpManager.getInstance().salesService;
	const money = (value: number | null | undefined) => value === null || value === undefined ? "-" : currency + value.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

	const [studentQuery, setStudentQuery] = useState<string>("");
	const debouncedStudent = useDebounced(studentQuery, 350);
	const [students, setStudents] = useState<SaleStudent[]>([]);
	const [studentSearchError, setStudentSearchError] = useState<string | null>(null);
	const [productSearchError, setProductSearchError] = useState<string | null>(null);
	const [context, setContext] = useState<SaleContext | null>(null);
	const [loadingStudent, setLoadingStudent] = useState<boolean>(false);
	const [productQuery, setProductQuery] = useState<string>("");
	const debouncedProduct = useDebounced(productQuery, 350);
	const [products, setProducts] = useState<SaleProduct[]>([]);
	const [scanNotice, setScanNotice] = useState<string | null>(null);
	const [cart, setCart] = useState<CartLine[]>([]);
	const [sellerIds, setSellerIds] = useState<string[]>([]);
	const [noSeller, setNoSeller] = useState<boolean>(false);
	const [note, setNote] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [done, setDone] = useState<{ kitIds: number[]; total: number; pending: number } | null>(null);
	const [paying, setPaying] = useState<boolean>(false);
	const [paidNotice, setPaidNotice] = useState<string | null>(null);
	const scanRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!campusId || context || debouncedStudent.trim().length < 2) { setStudents([]); setStudentSearchError(null); return; }
		let alive = true;
		setStudentSearchError(null);
		http.searchStudents(campusId, debouncedStudent.trim())
			.then((response) => { if (alive) setStudents(response.data); })
			.catch((requestError) => { if (alive) { setStudents([]); setStudentSearchError(apiError(requestError, "No se pudo buscar alumnos.")); } });
		return () => { alive = false; };
	}, [campusId, debouncedStudent, context]);

	useEffect(() => {
		if (!campusId || debouncedProduct.trim().length < 2) { setProducts([]); setProductSearchError(null); return; }
		let alive = true;
		setProductSearchError(null);
		http.searchProducts(campusId, debouncedProduct.trim())
			.then((response) => { if (alive) setProducts(response.data); })
			.catch((requestError) => { if (alive) { setProducts([]); setProductSearchError(apiError(requestError, "No se pudo buscar productos.")); } });
		return () => { alive = false; };
	}, [campusId, debouncedProduct]);

	const reset = () => {
		setContext(null); setStudentQuery(""); setStudents([]); setCart([]); setSellerIds([]); setNoSeller(false); setNote("");
		setProductQuery(""); setProducts([]); setDone(null); setPaidNotice(null); setError(null); setScanNotice(null);
	};

	const pickStudent = useCallback((studentId: number) => {
		setLoadingStudent(true);
		setError(null);
		http.studentContext(campusId, studentId).then((response) => {
			setContext(response.data);
			setStudents([]);
			setCart([]);
			setSellerIds([]);
			setNoSeller(false);
			setDone(null);
			setPaidNotice(null);
		}).catch((requestError) => setError(apiError(requestError, "No se pudo abrir al alumno."))).finally(() => setLoadingStudent(false));
	}, [campusId]);

	/** variantId: la variante del código escaneado (una etiqueta de Blanco · M vende el modelo con esa variante). */
	const add = (product: SaleProduct, variantId?: number | null) => {
		if (product.pricePending || done) return;
		const scanned = variantsOf(product).find((option) => option.id === variantId) || null;
		setCart((lines) => {
			// Each variant is its own line (Blanco · M y Negro · L del mismo producto); without a scanned variant it goes to the
			// line that has none yet. Scanning never turns pieces already in the cart into that variant.
			const index = lines.findIndex((line) => line.product.id === product.id && line.variantId === (scanned ? scanned.id : null));
			if (index >= 0) {
				const next = [...lines];
				next[index] = { ...next[index], quantity: next[index].quantity + 1 };
				return next;
			}
			return [...lines, { product, quantity: 1, size: "", variantId: scanned ? scanned.id : null, choices: {} }];
		});
	};

	const scan = async (event: React.FormEvent) => {
		event.preventDefault();
		const code = productQuery.trim();
		if (!code || done) return;
		setScanNotice(null);
		try {
			const response = await http.searchProducts(campusId, code, true);
			if (response.data.length === 1) {
				const hit = response.data[0];
				add(hit, hit.scannedVariantId);
				setProductQuery("");
				const scannedLabel = variantsOf(hit).find((option) => option.id === hit.scannedVariantId)?.label || hit.scannedSize;
				setScanNotice(hit.pricePending ? hit.name + ": precio por aprobar" : hit.name + (scannedLabel ? " " + scannedLabel : "") + " agregado");
			} else {
				setScanNotice("Ningún producto con el código " + code);
			}
		} catch (requestError) {
			setScanNotice(apiError(requestError, "No se pudo leer el código."));
		}
		scanRef.current?.focus();
	};

	const total = cart.reduce((sum, line) => sum + line.quantity * linePrice(line), 0);
	const sellerOptions = (context?.sellers || []).map((seller) => ({
		id: seller.id,
		name: seller.name,
		hint: seller.kind === "teacher" ? (seller.isStudentTeacher ? "Maestro del alumno" : "Maestro") : undefined
	}));
	const canCharge = !!context && cart.length > 0 && (noSeller || sellerIds.length > 0) && !busy && !idleLocked && perms?.create && !cart.some(mustPick);

	const charge = async () => {
		if (!context) return;
		setBusy(true);
		setError(null);
		try {
			const response = await http.charge(campusId, {
				studentId: context.student.id,
				lines: cart.map((line) => ({
					articleId: line.product.id, quantity: line.quantity, size: line.size.trim() || null, variantId: line.variantId,
					choices: Object.keys(line.choices).map((modelId) => ({ modelId: Number(modelId), variantId: line.choices[Number(modelId)] }))
				})),
				advisors: noSeller ? [] : sellerIds.map((id) => ({ userId: id })),
				note: note.trim()
			});
			setDone(response.data);
			setCart([]);
			http.studentContext(campusId, context.student.id).then((fresh) => setContext(fresh.data)).catch(() => undefined);
		} catch (requestError) {
			setError(apiError(requestError, "No se pudo cargar la venta."));
		} finally {
			setBusy(false);
		}
	};

	if (!campusId) return <div style={ui.page}>Selecciona un plantel.</div>;

	const stockLine = (product: SaleProduct) => {
		if (!product.tracksStock) return <span style={ui.tiny}>{product.legacy ? "Catálogo viejo" : product.kindLabel}</span>;
		const here = product.availableHere ?? 0;
		return <span style={{ ...ui.tiny, display: "inline-flex", gap: "8px", flexWrap: "wrap" }}>
			<span style={{ color: here > 0 ? "var(--wa-verde)" : "var(--wa-oro)", fontWeight: 700 }}>{here > 0 ? "Aquí " + here : "Sin existencia aquí"}</span>
			{product.committedHere ? <span>{product.committedHere} por entregar</span> : null}
			{product.elsewhere.length > 0 ? <span>{product.elsewhere.map((row) => row.code + " " + row.onHand).join(" · ")}</span> : null}
			{product.inTransit > 0 ? <span>En camino {product.inTransit}{product.eta ? " · " + product.eta : ""}</span> : null}
		</span>;
	};

	const productRow = (product: SaleProduct, extra?: React.ReactNode) => <div key={product.id}
		style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderBottom: "1px solid var(--wa-filo)", opacity: product.alreadyBought ? 0.6 : 1 }}>
		<div style={{ flex: 1, minWidth: 0 }}>
			<div style={{ fontWeight: 700, fontSize: "14px" }}>{product.name}{extra}</div>
			<div style={ui.tiny}>{product.waCode || product.sku}</div>
			<div>{stockLine(product)}</div>
		</div>
		<div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
			{product.pricePending ? <span style={{ ...ui.small, color: "var(--wa-oro)" }}>Precio por aprobar</span> : <b>{money(product.price)}</b>}
			{!product.pricePending && variantsOf(product).some((option) => option.price > product.price) ? <div style={ui.tiny}>hasta {money(Math.max(...variantsOf(product).map((option) => option.price)))}</div> : null}
		</div>
		<button type="button" className={ui.btn} disabled={idleLocked || product.pricePending || !context || !!done} title={done ? "Para otra venta toca Nueva Venta" : undefined} onClick={() => add(product)}>Agregar</button>
	</div>;

	return <div style={ui.page}>
		{paying && done && context ? <KitPaymentPrompt campusId={campusId} kitIds={done.kitIds} studentName={context.student.name} pendingAmount={done.pending}
			section="newSale" onDone={(intentId: number, message: string) => { setPaying(false); setPaidNotice(message); }} onCancel={() => setPaying(false)} /> : null}
		<h2 style={ui.title}>Nueva Venta</h2>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px" }}>{error}</div> : null}
		<div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-start", marginTop: "12px" }}>
			<div style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
				<div style={{ ...ui.card, padding: "12px" }}>
					{context ? <>
						<div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
							<div style={{ flex: 1 }}>
								<div style={{ fontSize: "18px", fontWeight: 700 }}>{context.student.name}</div>
								<div style={ui.small}>{[context.student.identityCode, context.student.phone].filter(Boolean).join(" · ")}</div>
							</div>
							<button type="button" className={ui.btn} disabled={idleLocked} onClick={reset}>Cambiar alumno</button>
						</div>
						{context.enrollments.length < 1 ? <div style={{ ...ui.small, marginTop: "6px" }}>Sin matrícula activa en este plantel</div> :
							context.enrollments.map((enrollment) => <div key={enrollment.id} style={{ fontSize: "13px", marginTop: "6px" }}>
								<b>{enrollment.course}</b>{enrollment.module && enrollment.module !== enrollment.course ? " · " + enrollment.module : ""}
								<span style={ui.small}> · {[enrollment.scheduleCode, enrollment.days, enrollment.hours, enrollment.mentor].filter(Boolean).join(" · ")}{enrollment.statusId !== 1 ? " · " + enrollment.status : ""}</span>
							</div>)}
						{context.bought.length > 0 ? <div style={{ marginTop: "8px" }}>
							<div style={ui.caption}>Ya tiene</div>
							{context.bought.slice(0, 6).map((row) => <div key={row.id} style={{ fontSize: "12px", display: "flex", justifyContent: "space-between", gap: "8px" }}>
								<span>{row.name} <span style={ui.tiny}>{row.date}</span></span>
								<span style={{ color: row.statusId === 0 ? "var(--wa-oro)" : "var(--wa-apagado)", whiteSpace: "nowrap" }}>{row.statusId === 0 ? "Sin pago" : row.status} · {money(row.paid)} / {money(row.total)}</span>
							</div>)}
						</div> : null}
					</> : <>
						<span style={ui.caption}>Alumno</span>
						<input className="form-control" style={{ ...ui.input, height: "38px", fontSize: FIELD_FONT === "16px" ? "16px" : "15px" }} autoFocus value={studentQuery} disabled={idleLocked || loadingStudent}
							placeholder="Nombre, teléfono o correlativo" onChange={(event) => setStudentQuery(event.target.value)} />
						{students.length > 0 ? <div style={{ marginTop: "6px", border: "1px solid var(--wa-filo)", borderRadius: "8px", maxHeight: "320px", overflowY: "auto" }}>
							{students.map((student) => <div key={student.id} onClick={() => pickStudent(student.id)}
								style={{ padding: "8px 10px", borderBottom: "1px solid var(--wa-filo)", cursor: "pointer", opacity: student.statusId === 1 ? 1 : 0.6 }}>
								<b>{student.name}</b> <span style={ui.small}>{[student.identityCode, student.phone].filter(Boolean).join(" · ")}</span>
								{student.courses.length > 0 ? <div style={ui.tiny}>{student.courses.join(" · ")}</div> : null}
							</div>)}
						</div> : studentSearchError ? <div style={{ ...ui.small, marginTop: "6px", color: "var(--wa-oro)" }}>{studentSearchError}</div>
							: debouncedStudent.trim().length >= 2 && !loadingStudent ? <div style={{ ...ui.small, marginTop: "6px" }}>Sin alumnos con esa búsqueda</div> : null}
					</>}
				</div>

				{context && context.proposed.length > 0 ? <div style={ui.card}>
					<div style={{ padding: "10px 12px 4px", fontWeight: 700 }}>Kit de su curso</div>
					{context.proposed.map((product) => productRow(product, <>
						{product.required ? <span style={{ ...ui.badge(false), background: "var(--wa-acento)", color: "var(--wa-superficie)" }}>Obligatorio</span> : null}
						{product.alreadyBought ? <span style={ui.badge(false)}>Ya lo compró</span> : product.alreadyCharged ? <span style={ui.badge(false)}>Cargado sin pago</span> : null}
					</>))}
				</div> : null}

				{context ? <div style={ui.card}>
					<form onSubmit={scan} style={{ padding: "10px 12px" }}>
						<span style={ui.caption}>Producto</span>
						<input ref={scanRef} className="form-control" style={ui.input} value={productQuery} disabled={idleLocked || !!done}
							placeholder="Buscar o escanear código" onChange={(event) => { setProductQuery(event.target.value); setScanNotice(null); }} />
						{done ? <div style={{ ...ui.small, marginTop: "4px" }}>Venta cargada. Para otra venta toca Nueva Venta.</div> : null}
						{scanNotice ? <div style={{ ...ui.small, marginTop: "4px" }}>{scanNotice}</div> : null}
						{productSearchError ? <div style={{ ...ui.small, marginTop: "4px", color: "var(--wa-oro)" }}>{productSearchError}</div> : null}
					</form>
					{products.map((product) => productRow(product))}
				</div> : null}
			</div>

			<div style={{ flex: "0 1 400px", minWidth: "300px", ...ui.card, padding: "12px", position: "sticky", top: "10px" }}>
				{done ? <>
					<div style={{ fontSize: "18px", fontWeight: 700, color: "var(--wa-verde)" }}>Cargado a la cuenta</div>
					<div style={{ fontSize: "26px", fontWeight: 700, margin: "6px 0" }}>{money(done.total)}</div>
					<div style={ui.small}>{context?.student.name} · se cobra en Caja</div>
					{done.pending > done.total ? <div style={{ ...ui.small, color: "var(--wa-oro)", marginTop: "4px" }}>Se sumó a lo que ya tenía cargado sin pago: {money(done.pending)} por cobrar de estos productos</div> : null}
					{paidNotice ? <div className="alert alert-success" style={{ marginTop: "10px", fontSize: "13px" }}>{paidNotice}</div> : null}
					<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
						{!paidNotice && perms?.create ? <button type="button" className={ui.btn} disabled={idleLocked} onClick={() => setPaying(true)}>Ya Pagó por Link o Depósito</button> : null}
						<button type="button" className={ui.btnPrimary} disabled={idleLocked} onClick={reset}>Nueva Venta</button>
					</div>
				</> : <>
					<div style={{ fontWeight: 700, marginBottom: "6px" }}>Venta</div>
					{cart.length < 1 ? <div style={{ ...ui.small, padding: "16px 0" }}>Sin productos</div> : cart.map((line, index) => <div key={line.product.id + "_" + index}
						style={{ padding: "8px 0", borderBottom: "1px solid var(--wa-filo)" }}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
							<b style={{ fontSize: "13px" }}>{line.product.name}</b>
							<span style={{ whiteSpace: "nowrap" }}>{money(line.quantity * linePrice(line))}</span>
						</div>
						<div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px", flexWrap: "wrap" }}>
							<button type="button" className={ui.btn} style={{ padding: "0 8px" }} disabled={idleLocked}
								onClick={() => setCart((lines) => line.quantity <= 1 ? lines.filter((_, i) => i !== index) : lines.map((l, i) => i === index ? { ...l, quantity: l.quantity - 1 } : l))}>−</button>
							<span style={{ minWidth: "18px", textAlign: "center" }}>{line.quantity}</span>
							<button type="button" className={ui.btn} style={{ padding: "0 8px" }} disabled={idleLocked}
								onClick={() => setCart((lines) => lines.map((l, i) => i === index ? { ...l, quantity: l.quantity + 1 } : l))}>+</button>
							{variantsOf(line.product).length > 0 ? <select className="form-select" style={{ ...ui.input, width: "190px", maxWidth: "100%" }} value={line.variantId ?? ""} disabled={idleLocked}
								onChange={(event) => setCart((lines) => lines.map((l, i) => i === index ? { ...l, variantId: event.target.value === "" ? null : Number(event.target.value) } : l))}>
								<option value="">{mustPick(line) ? "Elige talla / color" : "Talla / color al entregar"}</option>
								{variantsOf(line.product).map((option) => <option key={option.id} value={option.id}>
									{option.label}{Math.abs(option.price - line.product.price) >= 0.005 ? " · " + money(option.price) : ""}{line.product.tracksStock ? " · aquí " + option.onHand : ""}</option>)}
							</select> : (line.product.kind === "uniform" || line.product.kind === "kit") && !(line.product.kitChoices || []).length ? <input className="form-control" style={{ ...ui.input, width: "120px", height: "28px" }} value={line.size}
								placeholder="Talla" disabled={idleLocked} onChange={(event) => setCart((lines) => lines.map((l, i) => i === index ? { ...l, size: event.target.value } : l))} /> : null}
							<span style={{ ...ui.tiny, marginLeft: "auto" }}>{line.product.tracksStock && (line.product.availableHere ?? 0) < line.quantity ? "Se entrega cuando haya existencia" : ""}</span>
						</div>
						{mustPick(line) ? <div style={{ ...ui.tiny, color: "var(--wa-oro)", marginTop: "3px" }}>No todas las tallas cuestan lo mismo: elige una.</div> : null}
						{(line.product.kitChoices || []).map((piece) => <div key={piece.modelId} style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px", minWidth: 0 }}>
							<span style={{ ...ui.tiny, flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={piece.name}>{piece.name}{piece.quantity > 1 ? " ×" + piece.quantity : ""}</span>
							<select className="form-select" style={{ ...ui.input, width: "170px", maxWidth: "60%", flex: "0 0 auto", marginLeft: "auto" }} value={line.choices[piece.modelId] ?? ""} disabled={idleLocked}
								onChange={(event) => setCart((lines) => lines.map((l, i) => {
									if (i !== index) return l;
									const choices = { ...l.choices };
									if (event.target.value === "") delete choices[piece.modelId]; else choices[piece.modelId] = Number(event.target.value);
									return { ...l, choices };
								}))}>
								<option value="">Al entregar</option>
								{piece.variants.map((option) => <option key={option.id} value={option.id}>{option.label} · aquí {option.onHand}</option>)}
							</select>
						</div>)}
					</div>)}
					<div style={{ display: "flex", justifyContent: "space-between", fontSize: "18px", fontWeight: 700, margin: "10px 0" }}>
						<span>Total</span><span>{money(total)}</span>
					</div>
					{context ? <>
						<MultiSelect caption="Quién vendió" width="100%" options={sellerOptions} value={sellerIds} disabled={idleLocked || noSeller} allLabel="Elegir…" searchable
							onChange={(ids: string[]) => { setSellerIds(ids); if (ids.length > 0) setNoSeller(false); }} />
						<div onClick={() => { if (!idleLocked) { const next = !noSeller; setNoSeller(next); if (next) setSellerIds([]); } }}
							style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px", cursor: "pointer", fontSize: "13px" }}>
							<input type="checkbox" checked={noSeller} readOnly disabled={idleLocked} /> Sin asesora (venta directa)
						</div>
						<span style={{ ...ui.caption, display: "block", marginTop: "10px" }}>Nota</span>
						<textarea className="form-control" rows={2} style={{ fontSize: FIELD_FONT }} value={note} disabled={idleLocked} onChange={(event) => setNote(event.target.value)} />
					</> : null}
					<button type="button" className="btn btn-primary rounded-pill" style={{ width: "100%", marginTop: "12px" }} disabled={!canCharge} onClick={charge}>
						{busy ? "Cargando…" : "Cargar a la cuenta"}</button>
					{context && cart.length > 0 && !noSeller && sellerIds.length < 1 ? <div style={{ ...ui.small, color: "var(--wa-oro)", marginTop: "4px" }}>Elige quién vendió o marca "Sin asesora".</div> : null}
				</>}
			</div>
		</div>
	</div>;
};

export default NuevaVenta;
